// ABOUTME: SPEC-74 — the pure half of the logo soft reset: what the page does when the guest taps the
// ABOUTME: wordmark, and the two extra connect params that tell the backend this new sid is a reset.
//
// WHY (client feedback 2026-09-28, T6): "when he's stuck … it unfreezes". The wordmark starts the visit
// over WITHOUT a page refresh — a refresh would put the guest back on the enter screen (mic consent, an
// audio-unlock tap) — so the page does in place what a refresh does to the backend: a NEW session id.
// A new sid is what makes "the old conversation is not resumed" true by construction (SPEC-67/78 key
// everything by sid); the backend carries only the bag across (services/soft_reset.py).
//
// The socket is swapped, not closed-and-waited-for. `onclose` schedules its own backoff reconnect (1 s,
// plus a "reconnecting" banner) and a stuck socket may take the browser a long time to report closed, so
// the old socket's handlers are detached first and the SPEC-11 connect effect is re-fired at once via the
// nonce. Detaching is also what stops a late frame of the OLD session (a stage command, an audio chunk)
// from landing on the screen we just reset.

/** Two taps inside this window are one: a second reset before the first connected would carry the bag
 *  from a sid the server has not seen yet. */
export const SOFT_RESET_DEBOUNCE_MS = 2000;

/** The carousel chapter — the same id the backend's `return_to_reel` and the returning-guest boot use. */
export const SOFT_RESET_CHAPTER_ID = 'collection';

/** `OpenKind.SAME_PAGE` from ./sessionResume, spelled out so this module has no import cycle. */
const SAME_PAGE = 'same_page';

/**
 * Add `soft_reset=1&prev_sid=` to the connect URL while the announcement is still owed to ONE sid: the new
 * sid minted by the tap. The marker is tied to that sid the first time it is applied (`forSid`) and dies
 * when any of these happens — the new sid has opened once (`kind === same_page`, i.e. a blink of the NEW
 * visit); a different sid is being connected (a `force_reconnect` minted a third one, so `prev_sid` would
 * now name a stale bag); or the experience ended (see the hook). Without the sid tie, a force_reconnect
 * after the reset classified as `first` and re-sent the old params, copying the OLD bag over the guest's
 * live one and greeting them afresh a second time. Pure over `params`; mutates only `state`.
 * @param {URLSearchParams} params  already holds `session_id`
 * @param {{ pending: (null|{ prevSid: (string|null), forSid?: (string|null) }) }} state
 * @param {string} kind the sessionResume open kind for this connect
 */
export function applySoftResetParams(params, state, kind) {
  if (!state.pending) return;
  const sid = params.get('session_id');
  if (state.pending.forSid === undefined) state.pending.forSid = sid;
  if (kind === SAME_PAGE || state.pending.forSid !== sid) { state.pending = null; return; }
  params.set('soft_reset', '1');
  if (state.pending.prevSid) params.set('prev_sid', state.pending.prevSid);
}

/** A step that fails must never strand the guest: "stuck" is exactly why they tapped, so the identity swap
 *  below always runs. Logged by name only — never the error text. */
function safely(name, fn) {
  try { fn(); } catch (e) { console.warn(`[SPEC-74][RESET] step=${name} failed err=${e?.name || 'Error'}`); }
}

/** Tear the old socket away from the page: no handler may act on, or reschedule after, the old session. */
function detachOldSocket(ws) {
  const old = ws?.current;
  if (!old) return;
  old.onopen = null; old.onmessage = null; old.onclose = null; old.onerror = null;
  try { old.close(1000); } catch { /* CONNECTING/CLOSED sockets may throw — either way it is gone */ }
}

/**
 * Do the reset. Order matters: the screen and the agent are silenced first (instant feedback), then the
 * identity is replaced, then the socket is swapped last so the connect effect reads the new state.
 *
 * `d` (one object — the collaborators are App.js's own refs and setters):
 *   ws, sessionIdRef, reconnectAttemptsRef, reconnectTimerRef, setReconnectNonce, state (see above),
 *   sessionResume, stageMachine, introFinishedSentRef, introFinishedPendingRef, introSkippedForReturningRef,
 *   silenceAgent(), clearScreen(), forgetSessionId() (drops the stored sid; the effect mints a new one)
 * @returns {string|null} the sid the guest left
 */
export function performSoftReset(d) {
  const prevSid = d.sessionIdRef.current || null;
  safely('silenceAgent', d.silenceAgent);
  safely('clearScreen', d.clearScreen);
  // The carousel, no film, immediately: `transition: 'none'` applies at once and replays the chapter after
  // the commit that renders the reel (the socket's own reset at open then finds nothing left to do).
  safely('stage', () => d.stageMachine.handleStageCommand({
    action: 'return_to_reel', chapter_id: SOFT_RESET_CHAPTER_ID, transition: 'none',
  }));
  safely('forgetSessionId', d.forgetSessionId);
  d.sessionIdRef.current = null;
  d.sessionResume.onFreshSession();
  // No film will play, and the backend must be told so at CONNECT (it words the greeting off it).
  d.introSkippedForReturningRef.current = true;
  d.introFinishedSentRef.current = false;
  d.introFinishedPendingRef.current = false;
  d.state.pending = { prevSid };
  if (d.reconnectTimerRef.current) { clearTimeout(d.reconnectTimerRef.current); d.reconnectTimerRef.current = null; }
  d.reconnectAttemptsRef.current = 0;
  detachOldSocket(d.ws);
  d.setReconnectNonce((n) => n + 1);
  console.log(`[SPEC-74][RESET] outcome=started prev=${prevSid || 'none'}`);
  return prevSid;
}
